interface OnboardingLoadingProps {
  readonly title?: string;
  readonly subtitle?: string;
}

export function OnboardingLoading({
  title = 'Preparing your journey',
  subtitle = 'Discovering amazing destinations for you',
}: OnboardingLoadingProps) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-8">
      {/* Animated Globe/Travel Loader */}
      <div className="relative h-24 w-24">
        {/* Outer rotating ring with gradient */}
        <div className="absolute inset-0 animate-spin rounded-full [animation-duration:3s]">
          <svg viewBox="0 0 96 96" className="h-full w-full">
            <defs>
              <linearGradient id="loaderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#53b4f8" />
                <stop offset="50%" stopColor="#98fd68" />
                <stop offset="100%" stopColor="#ffea5e" />
              </linearGradient>
            </defs>
            <circle
              cx="48"
              cy="48"
              r="44"
              fill="none"
              stroke="url(#loaderGradient)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="200 80"
            />
          </svg>
        </div>

        {/* Inner pulsing circle */}
        <div className="absolute inset-4 animate-pulse rounded-full bg-gradient-to-br from-[#53b4f8]/20 via-[#98fd68]/20 to-[#ffea5e]/20 [animation-duration:2s]" />

        {/* Center plane icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <svg
            viewBox="0 0 24 24"
            className="text-link h-8 w-8 animate-bounce [animation-duration:2s]"
            fill="currentColor"
          >
            <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
          </svg>
        </div>

        {/* Orbiting dots */}
        <div className="absolute inset-0 animate-spin [animation-duration:4s]">
          <div className="bg-gradient-color-1 absolute top-0 left-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-[0_0_8px_rgba(83,180,248,0.6)]" />
        </div>
        <div className="absolute inset-0 animate-spin [animation-direction:reverse] [animation-duration:5s]">
          <div className="bg-gradient-color-2 absolute top-1/2 right-0 h-2 w-2 translate-x-1/2 -translate-y-1/2 rounded-full shadow-[0_0_8px_rgba(152,253,104,0.6)]" />
        </div>
        <div className="absolute inset-0 animate-spin [animation-duration:6s]">
          <div className="bg-gradient-color-3 absolute bottom-0 left-1/2 h-2 w-2 -translate-x-1/2 translate-y-1/2 rounded-full shadow-[0_0_8px_rgba(255,234,94,0.6)]" />
        </div>
      </div>

      {/* Text content */}
      <div className="text-center">
        <h3 className="font-ivypresto text-link mb-2 text-[28px] leading-tight tracking-wide">
          {title}
        </h3>
        <p className="label-md-w1 text-low-emphasis max-w-[280px]">{subtitle}</p>
      </div>

      {/* Animated travel path */}
      <div className="flex items-center gap-3">
        <div className="bg-gradient-color-1 h-1.5 w-1.5 animate-pulse rounded-full [animation-delay:0ms]" />
        <div className="from-gradient-color-1 via-gradient-color-2 to-gradient-color-3 h-8 w-[2px] rotate-[30deg] bg-gradient-to-b opacity-40" />
        <div className="bg-gradient-color-2 h-2 w-2 animate-pulse rounded-full [animation-delay:200ms]" />
        <div className="from-gradient-color-2 via-gradient-color-3 to-gradient-color-1 h-8 w-[2px] rotate-[30deg] bg-gradient-to-b opacity-40" />
        <div className="bg-gradient-color-3 h-2.5 w-2.5 animate-pulse rounded-full [animation-delay:400ms]" />
      </div>
    </div>
  );
}
